// Step 12-2 · B (owner card Step 12 V4, section B) — running note numbers per engagement, the screen side.
//   * shownNo(note)          the number the reader sees: the server's running number (printed_no, note_numbering.py);
//                            note_no is the note's KEY (storage, tags, test ids) and is shown only by an older server
//   * NotesNotPresented      the collapsed "Not presented" list under the notes: the empty notes (no figures, no text of the
//                            auditor's), each with its "Include" tick (PUT /fs/{id}/note-numbers/{key}/include — stored per
//                            engagement, Audit Trail row); the notes themselves open as above (renderNote), so text can be
//                            typed into one, which then presents it
//   * NoteLinkSelect         a KAM's note reference picked from the presented notes (stores note_key; the report prints the
//                            note's running number when it is generated) — or a typed number, as before
//   * NoteNumbersStaleBanner a draft report version generated before the note numbers changed: "Note numbers changed since
//                            this version — regenerate" (a Final version is never flagged: it keeps the text it stored)
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

export function shownNo(note) {
  if (!note) return "";
  return note.printed_no != null ? note.printed_no : note.note_no;
}

export function NotesNotPresented({ notes, included, engagementId, renderNote, onChanged, readOnly }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const list = notes || [];
  const ticked = included || [];      // notes presented only because the auditor ticked "include": untick here
  if (!list.length && !ticked.length) return null;
  const include = async (n, value) => {
    setBusy(n.note_key ?? n.note_no);
    try {
      await api.put(`/fs/${engagementId}/note-numbers/${n.note_key ?? n.note_no}/include`, { include: value });
      toast.success(t("s12b.notPresented.saved"));
      onChanged && onChanged();
    } catch (e) {
      toast.error(toErrMsg(e, t("s12b.notPresented.failed")));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="border-t bg-slate-50/40 dark:bg-slate-900/30" data-testid="notes-not-presented">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              className="flex w-full items-center gap-2 px-4 py-2 text-start text-sm font-medium text-muted-foreground"
              data-testid="notes-not-presented-toggle">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4 rtl:rotate-180" />}
        {t("s12b.notPresented.title").replace("{n}", String(list.length))}
      </button>
      {open && (
        <div data-testid="notes-not-presented-list">
          <p className="px-4 pb-2 text-[11px] text-muted-foreground">{t("s12b.notPresented.hint")}</p>
          {ticked.map((n) => (
            <label key={`inc-${n.note_no}`} className="flex items-center gap-2 px-4 pb-1 text-xs" data-testid={`note-included-${n.note_no}`}>
              <input type="checkbox" className="h-3.5 w-3.5" checked disabled={readOnly || busy != null}
                     onChange={(e) => include(n, e.target.checked)} data-testid={`note-include-${n.note_no}`} />
              <span className="font-mono text-[10px]">#{shownNo(n)}</span> {n.title} — {t("s12b.notPresented.include")}
            </label>
          ))}
          {list.map((n) => (
            <div key={n.note_no} className="flex items-start" data-testid={`note-not-presented-${n.note_no}`}>
              <label className="inline-flex shrink-0 items-center gap-1 ps-4 pt-2.5 text-[11px]" title={t("s12b.notPresented.includeTitle")}>
                <input type="checkbox" className="h-3.5 w-3.5" checked={false} disabled={readOnly || busy != null}
                       onChange={(e) => include(n, e.target.checked)} data-testid={`note-include-${n.note_no}`} />
                {t("s12b.notPresented.include")}
              </label>
              <div className="min-w-0 flex-1">{renderNote(n)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NoteNumbersStaleBanner({ engId, versionId, status }) {
  const { t } = useI18n();
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    let alive = true;
    setChanged(false);
    if (!engId || !versionId || status === "final") return undefined;
    api.get(`/fs/${engId}/note-numbers/report-versions/${versionId}`)
      .then((r) => { if (alive) setChanged(!!(r.data && r.data.changed)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [engId, versionId, status]);
  if (!changed) return null;
  return (
    <div role="status" className="mb-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
         data-testid="report-note-numbers-stale">
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      {t("s12b.report.stale")}
    </div>
  );
}

export function NoteLinkSelect({ engId, value, onChange, disabled, testId = "note-link" }) {
  const { t, lang } = useI18n();
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!engId) return undefined;
    api.get(`/fs/${engId}/note-numbers`, { _silent: true })
      .then((r) => { if (alive) setEntries(((r.data && r.data.entries) || []).filter((e) => e.group !== "general")); })
      .catch(() => {});
    return () => { alive = false; };
  }, [engId]);
  if (!entries.length) return null;
  const pick = (v) => {
    if (!v) { onChange({ note_key: "" }); return; }      // "" = no link (an edit sends it; null would keep the old link)
    const e = entries.find((x) => String(x.note_key) === v);
    onChange({ note_key: v, note_ref: e ? String(e.printed_no) : "" });
  };
  return (
    <select className="rounded border p-1 text-sm" value={value ? String(value) : ""} disabled={disabled}
            onChange={(e) => pick(e.target.value)} title={t("s12b.noteLink.hint")} data-testid={testId}>
      <option value="">{t("s12b.noteLink.typed")}</option>
      {entries.map((e) => (
        <option key={e.note_key} value={String(e.note_key)}>
          {t("s12b.noteLink.option").replace("{n}", String(e.printed_no)).replace("{title}", (lang === "ar" && e.title_ar) || e.title)}
        </option>
      ))}
    </select>
  );
}
